import { MANEDSNAVN, MANEDER, type Periodetype, type Rapporttype } from '@es/shared';

/**
 * Valgene på rapportsiden, og det som følger av dem.
 *
 * Rene funksjoner, så de kan prøves uten en nettleser: adressen rapporten
 * hentes fra, hvilken periode siden åpner på, og hvordan dekningen sies.
 */

export type Rapportomfang =
  { readonly type: 'omrade'; readonly id: string } | { readonly type: 'kommune' };

export interface Rapportvalg {
  readonly omfang: Rapportomfang;
  readonly ar: number;
  readonly type: Periodetype;
  /** Måneden eller tertialet. Null for årsrapporten. */
  readonly nummer: number | null;
}

/** Adressen i API-et, som JSON — eller som Excel. */
export function rapportsti(valg: Rapportvalg, excel = false): string {
  const base =
    valg.omfang.type === 'kommune'
      ? '/rapportering/okonomi/kommune'
      : `/rapportering/okonomi/omrader/${valg.omfang.id}`;
  const sporring = new URLSearchParams({ ar: String(valg.ar), type: valg.type });
  if (valg.type !== 'ar' && valg.nummer !== null) sporring.set('nummer', String(valg.nummer));
  return `${base}${excel ? '/excel' : ''}?${sporring.toString()}`;
}

/**
 * Perioden siden åpner på: den siste som er avsluttet.
 *
 * I oktober er det september som rapporteres for måned, og 2. tertial for
 * tertial — regnskapet for oktober er ikke ført ennå. I januar er det
 * desember og 3. tertial i fjor.
 */
export function standardnummer(
  type: Rapporttype,
  idag: Date,
): { ar: number; nummer: number | null } {
  const ar = idag.getFullYear();
  const maned = idag.getMonth() + 1;
  // Budsjettet lages høsten før året det gjelder.
  if (type === 'budsjett') return { ar: ar + 1, nummer: null };
  if (type === 'ar') return { ar: ar - 1, nummer: null };
  if (type === 'maned') return maned === 1 ? { ar: ar - 1, nummer: 12 } : { ar, nummer: maned - 1 };
  const ferdige = Math.floor((maned - 1) / 4);
  return ferdige === 0 ? { ar: ar - 1, nummer: 3 } : { ar, nummer: ferdige };
}

/** Hva som kan velges som periode for typen. Tomt for årsrapporten og budsjettet. */
export function periodevalg(type: Rapporttype): { verdi: number; tekst: string }[] {
  if (type === 'maned') {
    return MANEDER.map((m) => ({
      verdi: m,
      tekst: MANEDSNAVN[m].charAt(0).toUpperCase() + MANEDSNAVN[m].slice(1),
    }));
  }
  if (type === 'tertial')
    return [1, 2, 3].map((t) => ({ verdi: t, tekst: `${String(t)}. tertial` }));
  return [];
}
